import { useState, useCallback, useEffect } from 'react';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Autocomplete,
  Input,
  Textarea,
  Button,
  toast,
} from '@leadsnavi/ui-kit';
import { useForm } from 'react-hook-form';
import { useMemoizedFn } from 'ahooks';
import { Globe } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import CountrySelect from '@/common/base/country-select';
import Loader from '@/common/base/loader';
import useUserProfile from '@/stores/user-profile';
import useUserInfo from '@/stores/userStore';
import { savePost } from '@/services/swagger/easourcing/UserProfileService';
import { useConfirm } from '@/components/confirm-dialog/confirm-manager';
import { ProfileDetail, ProfileSchema } from '../dto/profile.dto';

export default function Profile() {
  const [loading, setLoading] = useState(false);
  const { closeDialog } = useUserProfile();
  const { reloadUserProfile, fetchProfileLoading, loading: userLoading } = useUserInfo();
  const confirm = useConfirm();

  const form = useForm<ProfileDetail>({
    resolver: zodResolver(ProfileSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      title: '',
      companyName: '',
      industry: '',
      homepage: '',
      location: '',
      companyIntroduction: '',
    },
    shouldFocusError: true, // 自动聚焦到第一个错误字段
    // criteriaMode: 'firstError', // 或 "all"
  });

  const { isDirty } = form.formState;

  const onSubmit = useCallback(
    async (data: ProfileDetail) => {
      try {
        setLoading(true);
        await savePost(data);
        toast.success('Profile saved successfully!');
        reloadUserProfile();
        closeDialog();
      } finally {
        setLoading(false);
      }
    },
    [closeDialog, reloadUserProfile]
  );

  const onCancel = useCallback(() => {
    if (isDirty) {
      confirm({
        title: 'Changes not yet saved. Discard changes and exit?',
        message: '',
        showCancel: true,
        okText: 'Discard Changes',
        cancelText: 'Continue Editing',
        danger: true,
      }).then(isConfirm => {
        if (isConfirm) {
          closeDialog();
        }
      });
      return;
    }
    closeDialog();
  }, [closeDialog, isDirty, confirm]);

  const initFormData = useMemoizedFn(() => {
    reloadUserProfile().then(data => {
      if (data) {
        form.reset({
          id: data.id || '',
          name: data.name || '',
          title: data.title || '',
          companyName: data.companyName || '',
          industry: data.industry || '',
          homepage: data.homepage || '',
          location: data.location || '',
          companyIntroduction: data.companyIntroduction || '',
        });
      }
    });
  });

  useEffect(() => {
    initFormData();
  }, [initFormData]);

  if (fetchProfileLoading || userLoading) {
    return (
      <div className="flex items-center justify-center h-20">
        <Loader />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="py-4 px-5 flex-1 overflow-auto">
        <Form {...form}>
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <span>
                        Name <span className="text-red-500 relative top-0.5">*</span>
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input size="lg" maxLength={100} allowClear placeholder="Enter your full name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex-1">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input size="lg" maxLength={200} allowClear placeholder="Enter your job title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* ------------------------------------------ */}
          <div className="flex gap-2 items-start pt-4">
            <div className="flex-1">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input size="lg" maxLength={200} allowClear placeholder="Enter your company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex-1">
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <FormControl>
                      <Autocomplete
                        placeholder="Select your industry"
                        options={[
                          { value: 'Textiles & Apparel' },
                          { value: 'Bags, Footwear & Accessories' },
                          { value: 'Jewelry, Eyewear & Watches' },
                          { value: 'Beauty' },
                          { value: 'Personal Care' },
                          { value: 'Sports & Entertainment' },
                          { value: 'Parents, Kids & Toys' },
                          { value: 'Pet Products' },
                          { value: 'Gifts & Crafts' },
                          { value: 'School & Office Supplies' },
                          { value: 'Home & Garden' },
                          { value: 'Furniture & Home Furnishings' },
                          { value: 'Home Appliances' },
                          { value: 'Lighting' },
                          { value: 'Packaging' },
                          { value: 'Industrial Machinery' },
                          { value: 'Commercial Equipment & Machinery' },
                          { value: 'Automation & Robotics Equipment' },
                          { value: 'Tools & Hardware' },
                          { value: 'Construction & Building Materials' },
                          { value: 'Consumer Electronics' },
                          { value: 'Electronic Components' },
                          { value: 'Electrical Equipment' },
                          { value: 'Batteries & Energy Storage Systems' },
                          { value: 'Renewable Energy' },
                          { value: 'Automotive Parts & Accessories' },
                          { value: 'Vehicles & Transportation' },
                          { value: 'Health & Medical' },
                          { value: 'Agriculture, Food & Beverage' },
                          { value: 'Chemicals & Materials' },
                          { value: 'OEM / Contract Manufacturing' },
                        ]}
                        filter={(options: { value: string }[], inputValue: string) => {
                          if (!inputValue) return options;
                          return options.filter(option =>
                            option.value.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        size="lg"
                        maxLength={100}
                        allowClear
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* ------------------------------------------ */}
          <div className="pt-4">
            <FormField
              control={form.control}
              name="homepage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Home Page</FormLabel>
                  <FormControl>
                    <Input
                      size="lg"
                      startSlot={<Globe size={16} color="#525252" />}
                      maxLength={1000}
                      allowClear
                      placeholder="https://"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ------------------------------------------ */}
          <div className="pt-4">
            <FormField
              control={form.control}
              name="companyIntroduction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Introduction</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-none max-h-[124px]"
                      size="lg"
                      rows={4}
                      maxLength={1000}
                      placeholder="Briefly describe your company"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ------------------------------------------ */}
          <div className="pt-4">
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span>
                      Location <span className="text-red-500 relative top-0.5">*</span>
                    </span>
                  </FormLabel>
                  <FormControl>
                    <CountrySelect placeholder="Select your location" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
      </div>
      <div className="flex justify-end space-x-2 p-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={form.handleSubmit(onSubmit)} loading={loading}>
          Save
        </Button>
      </div>
    </div>
  );
}
